"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

function AnimatedNumber({
  value,
  suffix = "",
  prefix = "",
}: {
  value: number;
  suffix?: string;
  prefix?: string;
}) {
  const [count, setCount] = useState(value);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (!inView || hasAnimated) return;
    setHasAnimated(true);
    setCount(0);
    const duration = 1500;
    const startTime = performance.now();

    function step(currentTime: number) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(eased * value);
      setCount(current);
      if (progress < 1) requestAnimationFrame(step);
    }

    requestAnimationFrame(step);
  }, [inView, value, hasAnimated]);

  return (
    <div
      ref={ref}
      className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-5xl font-extrabold text-transparent md:text-6xl"
    >
      {prefix}
      {count}
      {suffix}
    </div>
  );
}

function ProgressRing({ value, max, color }: { value: number; max: number; color: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const progress = (value / max) * circumference;

  return (
    <div ref={ref} className="relative mx-auto mb-4 h-24 w-24">
      <svg className="h-24 w-24 -rotate-90" viewBox="0 0 80 80">
        <circle
          cx="40"
          cy="40"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          className="text-white/5"
        />
        <motion.circle
          cx="40"
          cy="40"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={inView ? { strokeDashoffset: circumference - progress } : {}}
          transition={{ duration: 1.5, ease: "easeOut", delay: 0.2 }}
        />
      </svg>
    </div>
  );
}

const stats = [
  {
    value: 3,
    suffix: "x",
    label: "Meer trainingssessies per maand dan met live coaches",
    source: "Gemiddeld gebruik CloserAI-pilotdeelnemers",
    max: 5,
    color: "#3b82f6",
  },
  {
    value: 30,
    suffix: "%",
    label: "Snellere ramp-time voor nieuwe medewerkers",
    source: "Op basis van CloserAI-pilotresultaten",
    max: 100,
    color: "#6366f1",
  },
  {
    value: 5,
    prefix: "\u20AC",
    suffix: "K+",
    label: "Bespaard per kwartaal op externe trainers",
    source: "Vgl. 2 trainerdagen/maand \u00E0 \u20AC300/uur, team van 15",
    max: 10,
    color: "#8b5cf6",
  },
];

export default function AnimatedStats() {
  return (
    <div className="grid gap-8 text-center md:grid-cols-3">
      {stats.map((stat, i) => (
        <motion.div
          key={stat.label}
          initial={{ opacity: 0, filter: "blur(10px)", y: 20 }}
          whileInView={{ opacity: 1, filter: "blur(0px)", y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] }}
        >
          <ProgressRing value={stat.value} max={stat.max} color={stat.color} />
          <AnimatedNumber value={stat.value} suffix={stat.suffix} prefix={stat.prefix} />
          <p className="mt-3 text-gray-300">{stat.label}</p>
          <p className="mt-1 text-[11px] text-gray-500">{stat.source}</p>
        </motion.div>
      ))}
    </div>
  );
}
